import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Col,
  Divider,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
} from 'antd';
import { OrgCredential, OrgStatus } from '../../interfaces/organization';
import './OrganizationsSelector.less';
import AddOrganizationDialog from '../AddOrganizationDialog/AddOrganizationDialog';
import store, { AppDispatch, RootState } from '../../app/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  DeleteOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import {
  deleteCredential,
  fetchCredentials,
  refreshCredentials,
  selectCredential,
  verifyCredential,
} from '../../services/organizationService';
import { AuthMessageType, CirraServiceType } from '../../enums/auth';
import { getCredentialsToDisplay } from '../../utils/credentialUtils';
import { OrgEnvironmentType } from '../../enums/organization';
import { useNavigate } from 'react-router-dom';
import {
  setOrganization,
  setAllCredentials,
} from '../../app/Slices/organizationSlice';
import OrganizationItem from '../Organizations/OrganizationItem';
import { getAppLoaderController } from '../../hooks/loadingContext';
import { Notify } from '../../services/notificationService';
import { getReadonlyElementTitle } from '../../services/subscription';
import { selectIsSubscriptionReadonly } from '../../app/Slices/subscriptionSlice';
import { AppRoutes } from '../../constants/route';
import { confirmAction } from '../../utils/confirmActionModal';
import { ReactComponent as ExternalLinkIcon } from '../../assets/external-link.svg';
import { resetAdminAgent } from '../../app/Slices/adminAgentSlice';
import { setCopilotChatSessionId } from '../../services/copilotThread.service';
import { useRouting } from '../../hooks/useRouting';
import { useUserAccess } from '../../hooks/useUserAccess';

const ENVIRONMENT_COLOR_MAP: Record<OrgEnvironmentType, string> = {
  [OrgEnvironmentType.PRODUCTION]: 'blue',
  [OrgEnvironmentType.SANDBOX]: 'orange',
  [OrgEnvironmentType.CUSTOM]: 'magenta',
  [OrgEnvironmentType.SCRATCH]: 'purple',
  [OrgEnvironmentType.DEVELOPER]: 'purple',
};

const OrganizationsSelector: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isOnAdminAgentPage, isOnChangeAgentPage } = useRouting();
  const { currentAccount } = useUserAccess();
  const [selectedCredential, setSelectedCredential] =
    useState<OrgCredential | null>(null);
  const [credentials, setCredentials] = useState<OrgCredential[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [openModal, setOpenModal] = useState(false);
  const [openSettingsModal, setOpenSettingsModal] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [postMessage, setPostMessage] = useState<{
    type: AuthMessageType | '';
    message: string;
  }>({ type: '', message: '' });
  const { org, credentials: allCredentials } = useSelector(
    (state: RootState) => state.organization
  );
  const isReadonly = useSelector(selectIsSubscriptionReadonly);
  const isAppLoading = useSelector((state: RootState) => state.global.loading);

  useEffect(() => {
    manageCredentials(allCredentials);
    setLoading(false);
    // Note: currentAccount.cirra_user_id is needed in deps so filtering re-runs
    // when account loads after credentials (fixes shared org duplicate cards)
  }, [allCredentials, currentAccount?.cirra_user_id]);

  useEffect(() => {
    if (postMessage.type === AuthMessageType.SUCCESS) {
      Notify.success(postMessage.message);
    } else if (postMessage.type === AuthMessageType.ERROR) {
      Notify.error(postMessage.message, 0);
    }
  }, [postMessage]);

  useEffect(() => {
    setSelectedCredential(org);
  }, [org, credentials]);

  const handleFetchCredentials = async (silent = false) => {
    // Don't fetch if app is still loading (authentication in progress)
    if (isAppLoading) {
      console.debug('Skipping credentials fetch - app is still loading');
      return;
    }

    const { setAppLoading } = getAppLoaderController();
    try {
      if (!silent) {
        setAppLoading(true, !store.getState().organization.org, {
          title: 'Connecting to Your Salesforce Org...',
        });
      }
      const _credentials = await fetchCredentials();
      manageCredentials(_credentials);

      // Update Redux store with latest credentials
      dispatch(setAllCredentials(_credentials));
    } catch (error) {
      if (!silent) {
        Notify.error(
          (error as Error).message || 'Failed to fetch credentials',
          0
        );
      }
    } finally {
      setLoading(false);
      if (!silent) {
        setAppLoading(false);
      }
    }
  };

  const manageCredentials = (credentials: OrgCredential[]) => {
    // Get valid credentials and sort them by priority: main_selected_at > mcp_selected_at > selected_at
    // Pass currentAccount.cirra_user_id to filter out shared orgs when user already has a connection
    const sortedCredentials = getCredentialsToDisplay(
      credentials,
      ['main_selected_at', 'mcp_selected_at', 'selected_at'],
      currentAccount?.cirra_user_id
    );

    setCredentials(sortedCredentials);
    const recentCredential =
      sortedCredentials.length > 0 ? sortedCredentials[0] : null;
    dispatch(setOrganization(recentCredential));
  };

  const selectOrg = async (selectedCred: OrgCredential, sameOrg: boolean) => {
    try {
      const _credentials = sameOrg
        ? await fetchCredentials()
        : await selectCredential(selectedCred, CirraServiceType.AGENTS);
      setLoading(false);

      if (!sameOrg) {
        Notify.success('Salesforce organization is selected!');
        setCopilotChatSessionId('', 8); // reset thread ID
        dispatch(resetAdminAgent());
      }

      // Determine where to navigate based on current context
      let targetPath: string;
      if (isOnChangeAgentPage) {
        // If we're on change agent page, stay there
        targetPath = AppRoutes.changeRequests.path;
      } else if (isOnAdminAgentPage) {
        // If we're on admin agent page, stay there
        targetPath = AppRoutes.adminAgent.path;
      } else {
        // Default behavior: inactive orgs go to change requests, active orgs go to admin agent
        targetPath =
          selectedCred.status !== OrgStatus.ACTIVE
            ? AppRoutes.changeRequests.path
            : AppRoutes.adminAgent.path;
      }

      navigate(targetPath, { replace: true });
      manageCredentials(_credentials);
    } catch (error) {
      Notify.error(
        (error as Error).message ||
          'Failed to select Salesforce organization. Please try again.',
        0
      );
    }
  };

  const removeOrg = async () => {
    const confirmedRemove = await confirmAction({
      title: 'Remove Salesforce Org',
      content: 'Are you sure you want to remove this Salesforce Org?',
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
    });
    if (!confirmedRemove) {
      return;
    }

    setLoading(true);
    try {
      if (selectedCredential) {
        await deleteCredential(selectedCredential);
      }
      setOpenSettingsModal(false);
      dispatch(setOrganization(null));
      await handleFetchCredentials();
      navigate(AppRoutes.adminAgent.path);
    } catch (error) {
      setLoading(false);
      Notify.error(
        (error as Error).message || 'Failed to delete credentials',
        0
      );
    }
  };

  const refreshCred = useCallback(
    async ({ cred, sameOrg }: { cred: OrgCredential; sameOrg: boolean }) => {
      setLoading(true);
      const isActiveOrgUser = await verifyCredential(cred);
      if (isActiveOrgUser) {
        if (!sameOrg) {
          await selectOrg(cred, sameOrg);
        }
        setLoading(false);
        return false;
      }
      try {
        const confirmedRefresh = await confirmAction({
          title: 'Refresh authentication?',
          className: 'sf-org-reauthorize',
          content: (
            <>
              Authentication of{' '}
              <span style={{ whiteSpace: 'nowrap' }}>{cred.username}</span> is
              revoked or expired.
            </>
          ),
          okText: 'Reauthorize',
          cancelText: 'Continue without reauthorizing',
        });
        if (!confirmedRefresh) {
          await selectOrg(cred, sameOrg);
          setLoading(false);
          return false;
        }
        const refreshResult = await refreshCredentials(
          cred,
          CirraServiceType.AGENTS
        );
        if (refreshResult.success) {
          setPostMessage({
            type: AuthMessageType.SUCCESS,
            message: 'User has been authorized successfully!',
          });
          await selectOrg(cred, sameOrg);
          setLoading(false);
          return true;
        } else {
          if (postMessage.type === '') {
            await selectOrg(cred, sameOrg);
            setLoading(false);
          }
        }
      } catch (error) {
        setPostMessage({
          type: AuthMessageType.ERROR,
          message: (error as Error).message || 'Failed to refresh credentials',
        });
      }
      setLoading(false);
      return false;
    },
    [dispatch]
  );

  const onChangeCred = useCallback(
    async (selectedCredId: string) => {
      const selectedCred = credentials.find((c) => c.id === selectedCredId);
      if (selectedCred) {
        await refreshCred({ cred: selectedCred, sameOrg: false });
      }
    },
    [credentials, refreshCred]
  );

  const onClickCred = useCallback(
    async (selectedCredId: string) => {
      if (!selectedCredId || selectedCredId !== selectedCredential?.id) {
        return;
      }
      const selectedCred = credentials.find((c) => c.id === selectedCredId);
      if (!selectedCred) {
        return;
      }

      const refreshedSuccess = await refreshCred({
        cred: selectedCred,
        sameOrg: true,
      });
      if (refreshedSuccess) {
        const refreshedCred = { ...selectedCred, active: true };
        manageCredentials(
          credentials.map((c) =>
            c.id === refreshedCred.id ? refreshedCred : c
          )
        );
      }
    },
    [credentials, refreshCred]
  );

  const showModal = () => {
    setOpenModal(true);
    setOpenDropdown(false);
    setResetTrigger((prev) => prev + 1);
  };

  const showSettingsModal = () => {
    setOpenSettingsModal(true);
    setOpenDropdown(false);
  };

  const connectOrg = (org: OrgCredential | null) => {
    if (!org) {
      return;
    }
    window.open(
      `${org.instance_url}/lightning/setup/SetupOneHome/home`,
      '_blank'
    );
  };

  return (
    <div id="organizations-selector">
      <Select
        value={selectedCredential?.id}
        showSearch
        placeholder="Select a Salesforce Org"
        optionFilterProp="searchableValue"
        loading={loading}
        disabled={loading || isReadonly}
        onChange={onChangeCred}
        onSelect={onClickCred}
        open={openDropdown}
        onDropdownVisibleChange={(open) => setOpenDropdown(open)}
        dropdownRender={(menu) => (
          <>
            <span className="org-select-dropdown">
              {menu}
              <Divider style={{ margin: '8px 0' }} />
              <Space style={{ padding: '0 8px 4px' }}>
                <Tooltip
                  title={isReadonly ? getReadonlyElementTitle() : ''}
                  destroyOnHidden={false}
                >
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={showModal}
                    disabled={isReadonly}
                  >
                    Add Salesforce org
                  </Button>
                </Tooltip>
              </Space>
            </span>
          </>
        )}
        options={credentials
          .filter((credential) => credential.id && credential.username)
          .map((credential) => {
            return credential.id === selectedCredential?.id
              ? selectedCredential
              : credential;
          })
          .map((credential) => ({
            credential,
            value: credential.id,
            searchableValue: credential.username,
            label: (
              <OrganizationItem cred={credential} connectOrg={connectOrg} />
            ),
          }))}
        labelRender={({ label, value }) => {
          if (label) {
            return (
              <div className="org-item-container">
                <div className="name-container">{label}</div>
                {loading || openDropdown || !selectedCredential ? null : (
                  <div className="tag-container">
                    <Tag
                      color={
                        selectedCredential?.environment_type
                          ? ENVIRONMENT_COLOR_MAP[
                              selectedCredential?.environment_type as OrgEnvironmentType
                            ]
                          : 'default'
                      }
                    >
                      {selectedCredential?.environment_type}
                    </Tag>
                  </div>
                )}
              </div>
            );
          }
          return <span>No option match</span>;
        }}
      />
      {selectedCredential?.id && (
        <>
          <SettingOutlined
            title="Salesforce Org Settings"
            onClick={showSettingsModal}
          />
        </>
      )}

      <Modal
        title="Add Salesforce Org"
        open={openModal}
        onCancel={() => setOpenModal(false)}
        footer={null}
      >
        <AddOrganizationDialog
          service={CirraServiceType.AGENTS}
          onClose={() => setOpenModal(false)}
          resetTrigger={resetTrigger}
          setCredentials={(creds) => {
            setCredentials(creds);
            setOpenModal(false);
          }}
        />
      </Modal>
      <Modal
        title="Salesforce Org Info"
        open={openSettingsModal}
        onCancel={() => setOpenSettingsModal(false)}
        footer={null}
        width={600}
        className="org-settings-modal"
      >
        <CredentialRow label="Name" value={selectedCredential?.username} />
        <CredentialRow label="User ID" value={selectedCredential?.user_id} />
        <CredentialRow
          label="Org ID"
          value={selectedCredential?.organization_id}
        />
        <CredentialRow
          label="Org Type"
          value={selectedCredential?.environment_type}
        />
        <CredentialRow
          label="Instance URL"
          value={selectedCredential?.hostname}
        />
        <div className="org-settings-actions">
          <Button
            color="danger"
            variant="outlined"
            icon={<DeleteOutlined />}
            className="remove-org-btn"
            loading={loading}
            onClick={() => {
              removeOrg();
            }}
          ></Button>
          <Button
            type="primary"
            icon={<ExternalLinkIcon />}
            className="connect-org-btn"
            onClick={() => connectOrg(selectedCredential)}
          >
            Connect to Salesforce Org
          </Button>
        </div>
      </Modal>
    </div>
  );
};

interface InfoRowProps {
  label: string;
  value: string | undefined;
}

const CredentialRow: React.FC<InfoRowProps> = ({ label, value }) => (
  <Row>
    <Col span={6}>
      <p>{label}:</p>
    </Col>
    <Col span={18}>
      <p>{value || '-'}</p>
    </Col>
  </Row>
);

export default OrganizationsSelector;
